import type { ThemeMetadata } from '@/types/theme';

export const THEME_REGISTRY: Record<string, ThemeMetadata> = {
  // ── Legacy (original 10) ──────────────────────────────────────────────────
  'page-flip': {
    id: 'page-flip',
    displayName: 'Page Flip',
    description: 'Magazine-style two-column layout with page-turn visual rhythm',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#C9A84C',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Featured image, headline, subtitle, byline, publish date' },
      body: { required: true, description: 'Rich-text article content' },
      gallery: { required: false, description: 'Ordered image collection with captions', maxItems: 12 },
      footer: { required: false, description: 'Author bio, related articles, social share, tags' },
    },
    supports: { darkMode: true, rtl: true, schemaOrg: ['Article', 'BreadcrumbList'] },
    thumbnail: '/themes/page-flip/thumbnail.png',
  },
  'scrolling-long-form': {
    id: 'scrolling-long-form',
    displayName: 'Scrolling Long-Form',
    description: 'Long-form journalism with large pull-quotes, drop-caps, full-width image breaks',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#2D4A3E',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Featured image, headline, subtitle, byline, publish date' },
      body: { required: true, description: 'Rich-text long-form content' },
      gallery: { required: false, description: 'Inline images displayed as full-width breaks', maxItems: 8 },
      footer: { required: false, description: 'Author bio, related articles, social share, tags' },
    },
    supports: { darkMode: true, rtl: true, schemaOrg: ['Article'] },
    thumbnail: '/themes/scrolling-long-form/thumbnail.png',
  },
  'grid': {
    id: 'grid',
    displayName: 'Grid',
    description: 'Masonry-style content grid; works for listicles and roundups',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#E74C3C',
    complexity: 'medium',
    slots: {
      hero: { required: false, description: 'Featured hero spanning full grid width' },
      body: { required: true, description: 'Rich-text content displayed in grid cells' },
      gallery: { required: false, description: 'Image collection displayed in the grid', maxItems: 24 },
      footer: { required: false, description: 'Author bio, related articles, social share, tags' },
    },
    supports: { darkMode: true, rtl: true, schemaOrg: ['Article'] },
    thumbnail: '/themes/grid/thumbnail.png',
  },
  'photo-essay': {
    id: 'photo-essay',
    displayName: 'Photo Essay',
    description: 'Full-bleed photography with caption overlays and minimal chrome',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#1A1A1A',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Full-viewport cover image with headline overlay' },
      body: { required: false, description: 'Minimal text captions and narrative' },
      gallery: { required: true, description: 'Primary photo collection — the centrepiece of this layout', maxItems: 20 },
      footer: { required: false, description: 'Photographer credit, related essays, tags' },
    },
    supports: { darkMode: true, rtl: true, schemaOrg: ['Article'] },
    thumbnail: '/themes/photo-essay/thumbnail.png',
  },
  'news-column': {
    id: 'news-column',
    displayName: 'News Column',
    description: 'Traditional newspaper column layout with high information density',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#1B2A4A',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Headline, subheading, byline, dateline' },
      body: { required: true, description: 'Multi-column news article body' },
      gallery: { required: false, description: 'Inline images and photo captions', maxItems: 6 },
      footer: { required: false, description: 'Related articles, section tags' },
    },
    supports: { darkMode: true, rtl: true, schemaOrg: ['Article', 'BreadcrumbList'] },
    thumbnail: '/themes/news-column/thumbnail.png',
  },
  'magazine-grid': {
    id: 'magazine-grid',
    displayName: 'Magazine Grid',
    description: 'Editorial hero with asymmetric article grid and editorial feel',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#B8375E',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Large editorial hero occupying 2/3 width' },
      body: { required: true, description: 'Article body below the hero grid' },
      gallery: { required: false, description: 'Asymmetric article card grid above the fold', maxItems: 5 },
      footer: { required: false, description: 'Author info, related stories, tags' },
    },
    supports: { darkMode: true, rtl: true, schemaOrg: ['Article', 'BreadcrumbList'] },
    thumbnail: '/themes/magazine-grid/thumbnail.png',
  },
  'minimal-one-pager': {
    id: 'minimal-one-pager',
    displayName: 'Minimal One-Pager',
    description: 'Single-column, generous whitespace, reader-first typography',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#5B6E7C',
    complexity: 'simple',
    slots: {
      hero: { required: false, description: 'Optional headline and subtitle' },
      body: { required: true, description: 'Full article body in single-column layout' },
      gallery: { required: false, description: 'Optional inline images', maxItems: 4 },
      footer: { required: false, description: 'Minimal author attribution and tags' },
    },
    supports: { darkMode: true, rtl: true, schemaOrg: ['Article'] },
    thumbnail: '/themes/minimal-one-pager/thumbnail.png',
  },
  'timeline': {
    id: 'timeline',
    displayName: 'Timeline',
    description: 'Chronological narrative with event markers; suits retrospectives',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#6C4F3D',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Timeline title, date range, and summary' },
      body: { required: true, description: 'Chronological content divided into dated sections' },
      gallery: { required: false, description: 'Images associated with timeline events', maxItems: 10 },
      footer: { required: false, description: 'Sources, related timelines, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/timeline/thumbnail.png',
  },
  'data-viz': {
    id: 'data-viz',
    displayName: 'Data Viz',
    description: 'Data-journalism layout with text and chart slots at equal visual weight',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#2196F3',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Story headline, deck, and primary data point' },
      body: { required: true, description: 'Prose narrative; right-hand column on desktop' },
      gallery: { required: false, description: 'Chart figures (SVG or img); left-hand column on desktop', maxItems: 6 },
      footer: { required: false, description: 'Methodology notes, data sources, author info' },
    },
    supports: { darkMode: true, rtl: true, schemaOrg: ['Article'] },
    thumbnail: '/themes/data-viz/thumbnail.png',
  },
  'podcast-transcript': {
    id: 'podcast-transcript',
    displayName: 'Podcast Transcript',
    description: 'Audio-first layout with waveform slot and synchronized transcript body',
    version: '1.0.0',
    family: 'legacy',
    accentColor: '#9C27B0',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Episode title, host, guest, publish date, episode number' },
      body: { required: true, description: 'Full transcript text with timestamp annotations' },
      gallery: { required: false, description: 'Waveform visualisation image and episode artwork', maxItems: 2 },
      footer: { required: false, description: 'Show notes, chapter markers, subscribe links' },
    },
    supports: { darkMode: true, rtl: true, schemaOrg: ['Article'] },
    thumbnail: '/themes/podcast-transcript/thumbnail.png',
  },

  // ── Classic Print ─────────────────────────────────────────────────────────
  'economist': {
    id: 'economist',
    displayName: 'The Economist',
    description: 'Dense, opinionated editorial with signature red masthead and tight column grids',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#E3120B',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Headline, subheading, byline, dateline' },
      body: { required: true, description: 'Multi-column news body with pull-quote support' },
      gallery: { required: false, description: 'Inline editorial images', maxItems: 4 },
      footer: { required: false, description: 'Related articles, section tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article', 'BreadcrumbList'] },
    thumbnail: '/themes/economist/thumbnail.png',
  },
  'vogue': {
    id: 'vogue',
    displayName: 'Vogue',
    description: 'High-fashion editorial with oversized imagery, elegant serif typography, and gold accents',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#C0A060',
    complexity: 'rich',
    featured: true,
    slots: {
      hero: { required: true, description: 'Full-bleed fashion cover with bold overlaid headline' },
      body: { required: true, description: 'Editorial narrative with inline fashion shots' },
      gallery: { required: false, description: 'Fashion editorial spread', maxItems: 12 },
      footer: { required: false, description: 'Credits, related features, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/vogue/thumbnail.png',
  },
  'natgeo': {
    id: 'natgeo',
    displayName: 'National Geographic',
    description: 'Immersive photography-first storytelling with iconic yellow border and explorer aesthetic',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#FFCC00',
    complexity: 'rich',
    featured: true,
    slots: {
      hero: { required: true, description: 'Full-viewport photography with caption overlay and yellow rule' },
      body: { required: true, description: 'Long-form narrative with pull-quotes and drop-caps' },
      gallery: { required: true, description: 'Primary photography spread', maxItems: 16 },
      footer: { required: false, description: 'Photographer credits, related expeditions, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article', 'BreadcrumbList'] },
    thumbnail: '/themes/natgeo/thumbnail.png',
  },
  'financial-times': {
    id: 'financial-times',
    displayName: 'Financial Times',
    description: 'Classic salmon-pink broadsheet with structured finance and data journalism layout',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#C65B1B',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Headline, subheading, byline, dateline on salmon background' },
      body: { required: true, description: 'Article body with data callout boxes' },
      gallery: { required: false, description: 'Charts, graphs, and inline images', maxItems: 6 },
      footer: { required: false, description: 'Related articles, market tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article', 'BreadcrumbList'] },
    thumbnail: '/themes/financial-times/thumbnail.png',
  },
  'the-atlantic': {
    id: 'the-atlantic',
    displayName: 'The Atlantic',
    description: 'Intellectual long-form with clean serif type, deep navy accents, and measured pacing',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#1A3A5C',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Editorial hero with headline and department label' },
      body: { required: true, description: 'Long-form essay body with generous leading' },
      gallery: { required: false, description: 'Inline documentary images', maxItems: 6 },
      footer: { required: false, description: 'Author bio, related essays, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/the-atlantic/thumbnail.png',
  },
  'new-yorker': {
    id: 'new-yorker',
    displayName: 'The New Yorker',
    description: 'Iconic wit and culture journalism with cartoonist-style illustration slots and classic typefaces',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#C1272D',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Department header, headline, subheading, byline' },
      body: { required: true, description: 'Long-form prose with drop-cap and runover columns' },
      gallery: { required: false, description: 'Illustration and cartoon slots', maxItems: 4 },
      footer: { required: false, description: 'Author biography, related stories, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/new-yorker/thumbnail.png',
  },
  'rolling-stone': {
    id: 'rolling-stone',
    displayName: 'Rolling Stone',
    description: 'Rock-and-roll energy with full-bleed portraiture, bold headlines, and music culture aesthetic',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#E30613',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Full-bleed portrait with bold overlaid coverline' },
      body: { required: true, description: 'Narrative profile or review with pull-quotes' },
      gallery: { required: false, description: 'Photo spread and live performance shots', maxItems: 10 },
      footer: { required: false, description: 'Artist links, related reviews, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/rolling-stone/thumbnail.png',
  },
  'scientific-american': {
    id: 'scientific-american',
    displayName: 'Scientific American',
    description: 'Science communication layout with diagram slots, sidebar facts, and authoritative orange accent',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#FF6E00',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Science story headline, summary, and author credentials' },
      body: { required: true, description: 'Article body with sidebar callout support' },
      gallery: { required: false, description: 'Scientific diagrams, charts, and infographics', maxItems: 8 },
      footer: { required: false, description: 'References, related research, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/scientific-american/thumbnail.png',
  },
  'time-magazine': {
    id: 'time-magazine',
    displayName: 'Time Magazine',
    description: 'Iconic red-border cover design with bold portraiture and concise, high-impact editorial',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#CC0000',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Cover-style hero with red border frame and bold headline' },
      body: { required: true, description: 'Concise editorial body with key quotes' },
      gallery: { required: false, description: 'Portrait and documentary photography', maxItems: 6 },
      footer: { required: false, description: 'Person of the year links, related covers, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/time-magazine/thumbnail.png',
  },
  'harpers-bazaar': {
    id: 'harpers-bazaar',
    displayName: "Harper's Bazaar",
    description: 'Luxury fashion editorial with avant-garde typography, maximalist imagery, and deep crimson palette',
    version: '1.0.0',
    family: 'classic-print',
    accentColor: '#8B0000',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Luxury fashion hero with avant-garde headline treatment' },
      body: { required: true, description: 'Fashion feature body with product callouts' },
      gallery: { required: false, description: 'Haute couture editorial spread', maxItems: 14 },
      footer: { required: false, description: 'Brand credits, related features, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/harpers-bazaar/thumbnail.png',
  },

  // ── Digital-Native ────────────────────────────────────────────────────────
  'substack-letter': {
    id: 'substack-letter',
    displayName: 'Substack Letter',
    description: 'Newsletter intimacy with subscriber-first typography, inline CTAs, and personal voice',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#FF681A',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Newsletter title, issue number, author greeting' },
      body: { required: true, description: 'Letter body with inline subscription CTAs' },
      gallery: { required: false, description: 'Inline images and embedded content', maxItems: 6 },
      footer: { required: false, description: 'Subscribe link, author bio, archive link' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/substack-letter/thumbnail.png',
  },
  'ghost-minimal': {
    id: 'ghost-minimal',
    displayName: 'Ghost Minimal',
    description: 'Ultra-clean, content-first blogging aesthetic with generous whitespace and system fonts',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#15171A',
    complexity: 'simple',
    slots: {
      hero: { required: false, description: 'Optional featured image and title' },
      body: { required: true, description: 'Clean article body with minimal decoration' },
      gallery: { required: false, description: 'Inline images with caption', maxItems: 8 },
      footer: { required: false, description: 'Author card, tags, related posts' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/ghost-minimal/thumbnail.png',
  },
  'medium-reader': {
    id: 'medium-reader',
    displayName: 'Medium Reader',
    description: 'Distraction-free reading with clapping UX hints, member-wall notes, and reading-time badge',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#00AB6C',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Title, subtitle, author card with avatar and read time' },
      body: { required: true, description: 'Optimised reading column with highlights support' },
      gallery: { required: false, description: 'Inline images and embeds', maxItems: 8 },
      footer: { required: false, description: 'Author more stories, tags, clap count' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/medium-reader/thumbnail.png',
  },
  'mirror-essay': {
    id: 'mirror-essay',
    displayName: 'Mirror Essay',
    description: 'Web3-native essay format with wallet-collect hint, NFT edition badge, and cryptographic provenance',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#6C63FF',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Essay title, edition number, funding progress bar' },
      body: { required: true, description: 'Long-form essay body with backers list inline' },
      gallery: { required: false, description: 'Cover art and NFT preview images', maxItems: 4 },
      footer: { required: false, description: 'Collect button, subscriber list, related essays' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/mirror-essay/thumbnail.png',
  },
  'beehiiv-newsletter': {
    id: 'beehiiv-newsletter',
    displayName: 'Beehiiv Newsletter',
    description: 'Growth-focused newsletter with sponsor slot, referral ladder CTA, and emoji section dividers',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#F4B942',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Newsletter masthead, issue number, one-line summary' },
      body: { required: true, description: 'Newsletter body with sponsor callout block' },
      gallery: { required: false, description: 'Sponsor images and inline visuals', maxItems: 4 },
      footer: { required: false, description: 'Subscribe CTA, referral link, unsubscribe' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/beehiiv-newsletter/thumbnail.png',
  },
  'notion-doc': {
    id: 'notion-doc',
    displayName: 'Notion Doc',
    description: 'Structured document aesthetic with callout blocks, table-of-contents sidebar, and monospace accents',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#37352F',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Document title, cover image, emoji icon' },
      body: { required: true, description: 'Structured document body with callout and toggle blocks' },
      gallery: { required: false, description: 'Inline images and file embeds', maxItems: 8 },
      footer: { required: false, description: 'Last edited, collaborators, related pages' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/notion-doc/thumbnail.png',
  },
  'readwise-highlights': {
    id: 'readwise-highlights',
    displayName: 'Readwise Highlights',
    description: 'Quote-centric layout that foregrounds reader annotations and passage highlights',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#E8D5B7',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Source title, author, and resonance score' },
      body: { required: true, description: 'Highlights collection with annotation notes' },
      gallery: { required: false, description: 'Book cover and source imagery', maxItems: 2 },
      footer: { required: false, description: 'Full source link, saved date, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/readwise-highlights/thumbnail.png',
  },
  'indie-log': {
    id: 'indie-log',
    displayName: 'Indie Log',
    description: 'Developer-style build log with changelog format, commit-style entries, and monospace code blocks',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#4A90D9',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Product name, version, and build date' },
      body: { required: true, description: 'Changelog-style entries with code snippet support' },
      gallery: { required: false, description: 'Screenshots and product screenshots', maxItems: 6 },
      footer: { required: false, description: 'GitHub link, follow CTA, related updates' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/indie-log/thumbnail.png',
  },
  'paragraph-web3': {
    id: 'paragraph-web3',
    displayName: 'Paragraph Web3',
    description: 'Token-gated publishing aesthetic with on-chain proof badge and collector tiers',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#7C3AED',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Publication title, token-gate badge, edition info' },
      body: { required: true, description: 'Gated article body with unlock teaser at paywall' },
      gallery: { required: false, description: 'Cover art and collector badge images', maxItems: 4 },
      footer: { required: false, description: 'Collect edition, collector list, related publications' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/paragraph-web3/thumbnail.png',
  },
  'ghost-magazine': {
    id: 'ghost-magazine',
    displayName: 'Ghost Magazine',
    description: 'Premium membership magazine built on Ghost aesthetic — dark, editorial, subscription-first',
    version: '1.0.0',
    family: 'digital-native',
    accentColor: '#2B2D42',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Magazine-style hero with featured story and member badge' },
      body: { required: true, description: 'Long-form member article body' },
      gallery: { required: false, description: 'Editorial photography spread', maxItems: 8 },
      footer: { required: false, description: 'Member CTA, related features, author bio' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/ghost-magazine/thumbnail.png',
  },

  // ── Immersive / Cinematic ─────────────────────────────────────────────────
  'snow-fall': {
    id: 'snow-fall',
    displayName: 'Snow Fall',
    description: 'NYT-style parallax longread with cinematic scroll sequences, chapter markers, and embedded video',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#C8A870',
    complexity: 'rich',
    featured: true,
    slots: {
      hero: { required: true, description: 'Full-viewport cinematic hero with parallax depth layers' },
      body: { required: true, description: 'Long-form narrative with chapter scroll markers' },
      gallery: { required: true, description: 'Parallax image sequences and video embeds', maxItems: 20 },
      footer: { required: false, description: 'Credits, related longreads, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/snow-fall/thumbnail.png',
  },
  'cinematic-dark': {
    id: 'cinematic-dark',
    displayName: 'Cinematic Dark',
    description: 'Dark-mode cinematic layout with dramatic type, gold accent, and film-grain texture overlay',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#D4AF37',
    complexity: 'rich',
    featured: true,
    slots: {
      hero: { required: true, description: 'Dark full-bleed hero with cinematic title treatment' },
      body: { required: true, description: 'Article body on dark canvas with gold accents' },
      gallery: { required: false, description: 'Cinematic stills and atmospheric photography', maxItems: 10 },
      footer: { required: false, description: 'Cast/crew credits style, related articles, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/cinematic-dark/thumbnail.png',
  },
  'full-bleed-parallax': {
    id: 'full-bleed-parallax',
    displayName: 'Full-Bleed Parallax',
    description: 'Scroll-driven storytelling where background images shift at different rates to create depth',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#FF6B35',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Full-viewport parallax entry scene' },
      body: { required: true, description: 'Narrative with parallax section breaks' },
      gallery: { required: true, description: 'Parallax background image sequence', maxItems: 8 },
      footer: { required: false, description: 'Credits and related immersive stories' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/full-bleed-parallax/thumbnail.png',
  },
  'photo-essay-v2': {
    id: 'photo-essay-v2',
    displayName: 'Photo Essay v2',
    description: 'Evolved photo essay with diptych layouts, caption panels, and lightbox gallery integration',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#F5F5F5',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Cover photograph with pull-out caption' },
      body: { required: false, description: 'Minimal narrative text between image panels' },
      gallery: { required: true, description: 'Primary diptych and triptych photo panels', maxItems: 24 },
      footer: { required: false, description: 'Photographer bio, exhibition info, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/photo-essay-v2/thumbnail.png',
  },
  'gallery-wall': {
    id: 'gallery-wall',
    displayName: 'Gallery Wall',
    description: 'White-cube gallery aesthetic where each image is a framed artwork with museum-card metadata',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#2C2C2C',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Exhibition title and curatorial note' },
      body: { required: false, description: 'Exhibition essay text' },
      gallery: { required: true, description: 'Artwork collection with museum-card captions', maxItems: 20 },
      footer: { required: false, description: 'Gallery credits, artist bios, exhibition dates' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/gallery-wall/thumbnail.png',
  },
  'comic-strip': {
    id: 'comic-strip',
    displayName: 'Comic Strip',
    description: 'Panelled sequential art layout with speech-bubble body text and halftone textures',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#FF0000',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Comic title banner with issue number and genre tag' },
      body: { required: true, description: 'Sequential narrative text for speech bubbles' },
      gallery: { required: true, description: 'Comic panel images in strip order', maxItems: 24 },
      footer: { required: false, description: 'Creator credits, next issue CTA, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/comic-strip/thumbnail.png',
  },
  'zine-underground': {
    id: 'zine-underground',
    displayName: 'Zine Underground',
    description: 'DIY cut-and-paste zine aesthetic with rotated elements, ransom-note type, and xerox texture',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#FFE600',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Zine title in cut-up typography style' },
      body: { required: true, description: 'Manifesto or rant body with intentional disorder' },
      gallery: { required: false, description: 'Collage images and xerox-style illustrations', maxItems: 12 },
      footer: { required: false, description: 'Distro info, contact zine, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/zine-underground/thumbnail.png',
  },
  'brutalist': {
    id: 'brutalist',
    displayName: 'Brutalist',
    description: 'Raw HTML brutalism — visible borders, stark grids, deliberate ugliness as aesthetic choice',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#FF0000',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Brutal header with stark border treatment' },
      body: { required: true, description: 'Unforgiving column layout with raw typographic hierarchy' },
      gallery: { required: false, description: 'Images with harsh border frames', maxItems: 8 },
      footer: { required: false, description: 'Raw footer with border grid' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/brutalist/thumbnail.png',
  },
  'swiss-international': {
    id: 'swiss-international',
    displayName: 'Swiss International',
    description: 'International Typographic Style — grid system, Helvetica geometry, and mathematical negative space',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#E8080A',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Swiss grid headline with modular scale' },
      body: { required: true, description: 'Strictly grid-aligned body text' },
      gallery: { required: false, description: 'Geometric photography with grid crop', maxItems: 6 },
      footer: { required: false, description: 'Grid-aligned footer information' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/swiss-international/thumbnail.png',
  },
  'bauhaus-minimal': {
    id: 'bauhaus-minimal',
    displayName: 'Bauhaus Minimal',
    description: 'Form follows function — primary colours, geometric shapes, and radical typographic clarity',
    version: '1.0.0',
    family: 'immersive-cinematic',
    accentColor: '#E63B2E',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Bauhaus geometric hero composition' },
      body: { required: true, description: 'Minimal body with geometric callout shapes' },
      gallery: { required: false, description: 'Bauhaus-inspired graphic images', maxItems: 6 },
      footer: { required: false, description: 'Geometric footer grid' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/bauhaus-minimal/thumbnail.png',
  },

  // ── Cultural / Regional ───────────────────────────────────────────────────
  'afrofuturist': {
    id: 'afrofuturist',
    displayName: 'Afrofuturist',
    description: 'Pan-African speculative aesthetic with Ankara patterns, vibrant kente accents, and Afrocentric typography',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#FFB700',
    complexity: 'rich',
    featured: true,
    slots: {
      hero: { required: true, description: 'Bold Afrofuturist hero with pattern overlay' },
      body: { required: true, description: 'Long-form cultural narrative' },
      gallery: { required: false, description: 'Afrofuturist artwork and photography', maxItems: 10 },
      footer: { required: false, description: 'Cultural credits, related stories, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/afrofuturist/thumbnail.png',
  },
  'nikkei-grid': {
    id: 'nikkei-grid',
    displayName: 'Nikkei Grid',
    description: 'Japanese financial newspaper aesthetic with dense information grid and red-on-white precision',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#C40000',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Dense headline grid with category navigation' },
      body: { required: true, description: 'Multi-column financial news body' },
      gallery: { required: false, description: 'Charts and financial data visualisations', maxItems: 6 },
      footer: { required: false, description: 'Market data footer, related articles' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article', 'BreadcrumbList'] },
    thumbnail: '/themes/nikkei-grid/thumbnail.png',
  },
  'latin-editorial': {
    id: 'latin-editorial',
    displayName: 'Latin Editorial',
    description: 'Latin American magazine energy with vibrant colour, bold serif display, and passionate visual rhythm',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#E85D04',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Vibrant editorial hero with bold display serif' },
      body: { required: true, description: 'Passionate narrative with cultural references' },
      gallery: { required: false, description: 'Vibrant Latin photography', maxItems: 8 },
      footer: { required: false, description: 'Cultural credits, related stories, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/latin-editorial/thumbnail.png',
  },
  'nordic-minimal': {
    id: 'nordic-minimal',
    displayName: 'Nordic Minimal',
    description: 'Scandinavian design sensibility — hygge whitespace, muted blues, and functional elegance',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#5B8DB8',
    complexity: 'simple',
    slots: {
      hero: { required: false, description: 'Calm hero with restrained typography' },
      body: { required: true, description: 'Generous whitespace single-column body' },
      gallery: { required: false, description: 'Nature and lifestyle photography', maxItems: 6 },
      footer: { required: false, description: 'Minimal Nordic footer, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/nordic-minimal/thumbnail.png',
  },
  'middle-eastern-rtl': {
    id: 'middle-eastern-rtl',
    displayName: 'Middle Eastern RTL',
    description: 'Arabic-first RTL editorial with calligraphic display, warm desert palette, and geometric ornament',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#C8883A',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'RTL hero with Arabic calligraphic headline style' },
      body: { required: true, description: 'Right-to-left article body with arabesque dividers' },
      gallery: { required: false, description: 'Middle Eastern cultural photography', maxItems: 8 },
      footer: { required: false, description: 'Bilingual footer, related articles' },
    },
    supports: { darkMode: false, rtl: true, schemaOrg: ['Article'] },
    thumbnail: '/themes/middle-eastern-rtl/thumbnail.png',
  },
  'mediterranean-warm': {
    id: 'mediterranean-warm',
    displayName: 'Mediterranean Warm',
    description: 'Sun-drenched coastal aesthetic with terracotta tones, olive typography, and relaxed editorial pace',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#E07B39',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Warm coastal hero with terracotta accents' },
      body: { required: true, description: 'Leisurely travel or lifestyle narrative' },
      gallery: { required: false, description: 'Sun-drenched photography', maxItems: 10 },
      footer: { required: false, description: 'Cultural notes, related destinations, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/mediterranean-warm/thumbnail.png',
  },
  'south-asian-vibrant': {
    id: 'south-asian-vibrant',
    displayName: 'South Asian Vibrant',
    description: 'Bollywood-meets-editorial with deep jewel tones, ornate border motifs, and festive typographic energy',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#FF5733',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Jewel-toned hero with ornate border frame' },
      body: { required: true, description: 'Vibrant editorial body with decorative dividers' },
      gallery: { required: false, description: 'South Asian cultural and fashion photography', maxItems: 12 },
      footer: { required: false, description: 'Cultural credits, festival links, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/south-asian-vibrant/thumbnail.png',
  },
  'pan-african-earthy': {
    id: 'pan-african-earthy',
    displayName: 'Pan-African Earthy',
    description: 'Ubuntu philosophy in layout form — earthy terracotta, communal warmth, and African editorial voice',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#8B4513',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Earthy hero with communal photography' },
      body: { required: true, description: 'Pan-African narrative with cultural context' },
      gallery: { required: false, description: 'African landscapes and community photography', maxItems: 10 },
      footer: { required: false, description: 'Community credits, related stories, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/pan-african-earthy/thumbnail.png',
  },
  'colonial-archive': {
    id: 'colonial-archive',
    displayName: 'Colonial Archive',
    description: 'Archival newspaper aesthetic repurposed for decolonial storytelling — sepia, aged paper, critical framing',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#8B7355',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Archival-style masthead with critical editorial framing' },
      body: { required: true, description: 'Historical narrative on aged-paper background' },
      gallery: { required: false, description: 'Archival photographs and documents', maxItems: 8 },
      footer: { required: false, description: 'Historical sources, related archive pieces, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/colonial-archive/thumbnail.png',
  },
  'afropunk-digital': {
    id: 'afropunk-digital',
    displayName: 'Afropunk Digital',
    description: 'Afropunk meets digital subculture — neon on black, glitch aesthetics, and unapologetically loud type',
    version: '1.0.0',
    family: 'cultural-regional',
    accentColor: '#00FF00',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Neon hero with glitch treatment and bold punk headline' },
      body: { required: true, description: 'Manifesto-style body on dark canvas' },
      gallery: { required: false, description: 'Concert photography and digital art', maxItems: 12 },
      footer: { required: false, description: 'Scene credits, related music features, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/afropunk-digital/thumbnail.png',
  },

  // ── Functional / Specialist ───────────────────────────────────────────────
  'qa-interview': {
    id: 'qa-interview',
    displayName: 'Q&A Interview',
    description: 'Alternating question-answer layout with interviewer/subject visual distinction and pull-quotes',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#0066CC',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Interviewee portrait, name, role, and interview context' },
      body: { required: true, description: 'Q&A transcript with alternating Q/A visual treatment' },
      gallery: { required: false, description: 'Subject photography and context images', maxItems: 6 },
      footer: { required: false, description: 'Interviewer bio, related interviews, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/qa-interview/thumbnail.png',
  },
  'podcast-v2': {
    id: 'podcast-v2',
    displayName: 'Podcast v2',
    description: 'Next-generation podcast layout with chapter navigation, waveform visualiser, and transcript sync',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#9B59B6',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Episode cover, title, host list, and chapter count' },
      body: { required: true, description: 'Synced transcript with chapter headings and timestamps' },
      gallery: { required: false, description: 'Show artwork and guest portraits', maxItems: 4 },
      footer: { required: false, description: 'Listen links, subscribe CTA, show notes' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/podcast-v2/thumbnail.png',
  },
  'data-dashboard': {
    id: 'data-dashboard',
    displayName: 'Data Dashboard',
    description: 'Analytics-grade editorial with KPI cards, chart grid, and data-story narrative pane',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#2ECC71',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Dashboard title, date range, and KPI summary row' },
      body: { required: true, description: 'Data narrative and methodology explanation' },
      gallery: { required: true, description: 'Chart and graph grid — the centrepiece', maxItems: 12 },
      footer: { required: false, description: 'Data sources, update schedule, methodology notes' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/data-dashboard/thumbnail.png',
  },
  'legal-document': {
    id: 'legal-document',
    displayName: 'Legal Document',
    description: 'Court-filing aesthetic with numbered sections, definition boxes, and clause cross-references',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#2C3E50',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Document title, case number, parties, and date filed' },
      body: { required: true, description: 'Numbered clauses and legal body text' },
      gallery: { required: false, description: 'Exhibit images and referenced documents', maxItems: 6 },
      footer: { required: false, description: 'Signature block, exhibit index, filing stamp' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/legal-document/thumbnail.png',
  },
  'recipe-lifestyle': {
    id: 'recipe-lifestyle',
    displayName: 'Recipe & Lifestyle',
    description: 'Food editorial with hero photography, ingredient card, method steps, and nutrition callout',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#E74C3C',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Food photography hero with recipe name and cuisine tag' },
      body: { required: true, description: 'Ingredients list and numbered method steps' },
      gallery: { required: false, description: 'Step-by-step process and plating photography', maxItems: 8 },
      footer: { required: false, description: 'Author kitchen notes, related recipes, tags' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/recipe-lifestyle/thumbnail.png',
  },
  'travel-guide': {
    id: 'travel-guide',
    displayName: 'Travel Guide',
    description: 'Destination-first layout with map callout, practical info boxes, and vivid destination photography',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#27AE60',
    complexity: 'rich',
    featured: true,
    slots: {
      hero: { required: true, description: 'Destination hero photography with location overlay and tagline' },
      body: { required: true, description: 'Narrative guide with practical info callout boxes' },
      gallery: { required: true, description: 'Destination photography and attraction images', maxItems: 16 },
      footer: { required: false, description: 'Getting there, useful links, related guides' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article', 'BreadcrumbList'] },
    thumbnail: '/themes/travel-guide/thumbnail.png',
  },
  'sports-recap': {
    id: 'sports-recap',
    displayName: 'Sports Recap',
    description: 'Match report format with score bug, timeline events, player ratings grid, and action photography',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#F39C12',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Score bug, teams, venue, and match date' },
      body: { required: true, description: 'Match narrative with key moment timeline' },
      gallery: { required: false, description: 'Action photography and goal celebrations', maxItems: 10 },
      footer: { required: false, description: 'Player ratings, related match reports, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/sports-recap/thumbnail.png',
  },
  'event-liveblog': {
    id: 'event-liveblog',
    displayName: 'Event Liveblog',
    description: 'Reverse-chronological live coverage with timestamped updates, breaking badges, and pinned top story',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#E91E63',
    complexity: 'medium',
    slots: {
      hero: { required: true, description: 'Event name, live badge, and pinned top development' },
      body: { required: true, description: 'Reverse-chronological update stream with timestamps' },
      gallery: { required: false, description: 'Breaking news images and event photography', maxItems: 12 },
      footer: { required: false, description: 'Related coverage, event archives, tags' },
    },
    supports: { darkMode: true, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/event-liveblog/thumbnail.png',
  },
  'annual-report': {
    id: 'annual-report',
    displayName: 'Annual Report',
    description: 'Corporate publishing with executive summary, KPI table, chapter navigation, and brand-forward design',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#1565C0',
    complexity: 'rich',
    slots: {
      hero: { required: true, description: 'Organisation name, year, and CEO message summary' },
      body: { required: true, description: 'Report body with section chapters and KPI tables' },
      gallery: { required: false, description: 'Infographics, team photographs, project imagery', maxItems: 12 },
      footer: { required: false, description: 'Board signatures, auditor note, contact details' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/annual-report/thumbnail.png',
  },
  'academic-paper': {
    id: 'academic-paper',
    displayName: 'Academic Paper',
    description: 'Scholarly layout with abstract block, numbered sections, inline citations, and figure captions',
    version: '1.0.0',
    family: 'functional-specialist',
    accentColor: '#546E7A',
    complexity: 'simple',
    slots: {
      hero: { required: true, description: 'Paper title, authors, affiliations, abstract, and keywords' },
      body: { required: true, description: 'Academic body with numbered sections and footnotes' },
      gallery: { required: false, description: 'Figures, tables, and data charts with captions', maxItems: 10 },
      footer: { required: false, description: 'References, acknowledgements, DOI, supplementary materials' },
    },
    supports: { darkMode: false, rtl: false, schemaOrg: ['Article'] },
    thumbnail: '/themes/academic-paper/thumbnail.png',
  },
};

export const THEME_IDS: string[] = Object.keys(THEME_REGISTRY);
